import { useEffect, useState } from 'react';
import type { ListaNotas, Nota } from '../../../api/ops/clientes';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Chip } from '../../../componentes/Chip';
import { EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { estadoDeError } from '../../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import type { ContextoClientes } from '../lista/contexto';
import { AYUDA_NOTAS, PERMISOS, diaCorto, textoDeError } from '../lista/textos';

/**
 * Notas (2.3): con quien la escribió y su referencia («Javier Ruiz • 3 oct • Reserva del 3 oct», G145), la ayuda de G194
 * y archivar al momento (no se borran). «Ver las archivadas» sin cifra (G140). Escribe y archiva quien tiene
 * `clientes.notas.escribir`; el profesional, con «lo suyo», ve y escribe solo las de sus citas (lo filtra la API).
 */
export function Notas({ c, id }: { c: ContextoClientes; id: string }) {
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<ListaNotas | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [texto, setTexto] = useState('');
    const [errorTexto, setErrorTexto] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [archivadas, setArchivadas] = useState(false);
    const { ops, negocio, permisos, avisar } = c;
    const puedeEscribir = permisos.puede(PERMISOS.notas) && !c.soloLectura;

    useEffect(() => {
        let vivo = true;
        ops.listCustomerNotes(negocio, id, { cursor: pag.cursor, include_archived: true })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, pag.cursor, intento]);

    const anadir = () => {
        const cuerpo = texto.trim();
        if (cuerpo === '') {
            setErrorTexto('Escribe la nota.');
            return;
        }
        setOcupado(true);
        ops.createCustomerNote(negocio, id, { body: cuerpo })
            .then(() => {
                setTexto('');
                setErrorTexto('');
                pag.reiniciar();
                setIntento((i) => i + 1);
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido añadir la nota. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const archivar = (n: Nota) => {
        ops.archiveCustomerNote(negocio, id, n.id)
            .then((archivada) => {
                setLista((l) => (l === null ? l : { ...l, data: l.data.map((x) => (x.id === n.id ? archivada : x)) }));
                avisar({ tipo: 'ok', texto: 'Nota archivada.' });
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido archivar la nota. Prueba de nuevo.') });
            });
    };

    if (error !== null && lista === null) {
        return <EstadoError seccion="las notas" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={c.movil} />;
    }

    const notas = (lista?.data ?? []).filter((n) => archivadas || n.archived_at == null);
    const hayArchivadas = (lista?.data ?? []).some((n) => n.archived_at != null);

    return (
        <div className="rv-cl-seccion">
            {puedeEscribir && (
                <div className="rv-cl-nota-nueva">
                    <Campo
                        etiqueta="Nota nueva"
                        valor={texto}
                        onChange={(v) => {
                            setTexto(v);
                            setErrorTexto('');
                        }}
                        filas={2}
                        max={5000}
                        error={errorTexto}
                        ayuda={AYUDA_NOTAS}
                        nombre="nota"
                    />
                    <Boton tipo="secundario" icono="plus" movil={c.movil} ocupado={ocupado} ocupadoTexto="Añadiendo…" onClick={anadir}>
                        Añadir nota
                    </Boton>
                </div>
            )}
            {lista === null ? null : notas.length === 0 ? (
                <p className="rv-cl-vacio">Todavía no hay notas.</p>
            ) : (
                <div className="rv-cl-lineas">
                    {notas.map((n) => (
                        <div key={n.id} className="rv-cl-entrada rv-cl-entrada--nota">
                            <span className="rv-cl-linea">
                                <span className="rv-cl-linea__texto">{n.body}</span>
                                <span>
                                    {`${n.author_name} • ${diaCorto(n.created_at)}`}
                                    {n.reference?.label != null && n.reference.label !== '' && (
                                        <span className="rv-cl-referencia">
                                            <Icono nombre="link" />
                                            {n.reference.label}
                                        </span>
                                    )}
                                </span>
                            </span>
                            {n.archived_at != null ? (
                                <Chip estado="neutral">Archivada</Chip>
                            ) : (
                                puedeEscribir && (
                                    <Boton
                                        tipo="secundario"
                                        icono="archive"
                                        movil={c.movil}
                                        onClick={() => {
                                            archivar(n);
                                        }}
                                    >
                                        Archivar
                                    </Boton>
                                )
                            )}
                        </div>
                    ))}
                </div>
            )}
            {lista !== null && (pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={c.movil} />
            )}
            {hayArchivadas && (
                <button
                    type="button"
                    className="rv-cl-enlace"
                    onClick={() => {
                        setArchivadas((a) => !a);
                    }}
                >
                    <Icono nombre={archivadas ? 'eye-off' : 'archive'} />
                    {archivadas ? 'Ocultar las archivadas' : 'Ver las archivadas'}
                </button>
            )}
            <p className="rv-cl-nota rv-cl-nota--icono">
                <Icono nombre="lock" />
                Solo las ve tu equipo. No se borran: se archivan.
            </p>
        </div>
    );
}
